import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import type {
  BillingInvoice,
  Organisation,
  OrganisationMember,
  MyOrganisationMembership,
  CreateOrganisationGroupRequest,
  OrganisationBillingResponse,
  OrganisationBillingSummary,
  OrganisationCheckoutResponse,
  OrganisationCreditPolicyResponse,
  OrganisationCreditsOverviewResponse,
  OrganisationGroup,
  OrganisationGroupResponse,
  OrganisationGroupsResponse,
  OrganisationInvoicesResponse,
  OrganisationPaymentCardsResponse,
  SetOrganisationCreditPolicyOverrideRequest,
  UpdateOrganisationGroupRequest,
  UpdateOrganisationPaymentCardsRequest,
  OrganisationSeatPlan,
} from '@dreamai/types';
import { codedErrorMessage } from './organisationCreditLabels';
import { httpStatus } from './handbookTree';
import OrganisationGroups, { GroupOverrideInput } from './OrganisationGroups';
import OrganisationSubscription, {
  isSubscriptionLive,
} from './OrganisationSubscription';
import { seatMembers } from './organisationBillingLabels';
import OrganisationCardPanel from './OrganisationCardPanel';
import OrganisationPaymentCardLine from './OrganisationPaymentCardLine';
import {
  CARD_PURPOSE_LABELS,
  CardAssignments,
  ORGANISATION_CARD_PURPOSES,
  OrganisationCardPurpose,
  OrganisationPaymentCard,
  cardLabel,
} from './organisationPaymentCards';
import { OrganisationAdminTab, organisationTabPath } from './organisationTabs';
import './organisation-credits.scss';

interface OrganisationCreditsPanelProps {
  organisation: Organisation;
  /** The viewer's own seat; absent for admins without one */
  membership?: MyOrganisationMembership;
  members: OrganisationMember[];
  isAdmin: boolean;
  /** Which admin tab is showing; ignored for members */
  view: OrganisationAdminTab;
  /** Lets the parent (Members tab) show and edit group membership */
  onGroupsChange?: (groups: OrganisationGroup[]) => void;
  /**
   * Whether the organisation can use Groups at all: false while loading and
   * when the backend says the feature is not available (404/403).
   */
  onGroupsAvailabilityChange?: (available: boolean) => void;
  /** Whether members can be invited right now (needs a live subscription) */
  onCanInviteMembersChange?: (canInvite: boolean) => void;
  /** Price of one seat, for the invite dialog; null until billing loads */
  onSeatPlanChange?: (seatPlan: OrganisationSeatPlan | null) => void;
}

type Feedback = { type: 'success' | 'error'; message: string } | null;

interface PaymentCards {
  cards: OrganisationPaymentCard[];
  assignments: CardAssignments;
}

const NO_CARDS: PaymentCards = {
  cards: [],
  assignments: { subscription: null, topUps: null, memberSpending: null },
};

/**
 * Groups, the seat subscription and purchases for an organisation.
 *
 * Admins see one tab at a time (groups, plan & billing, purchases); the data
 * for all of them is loaded once so switching tabs is instant. Members see
 * only their purchases.
 */
const OrganisationCreditsPanel: FC<OrganisationCreditsPanelProps> = ({
  organisation,
  membership,
  members,
  isAdmin,
  view,
  onGroupsChange,
  onGroupsAvailabilityChange,
  onCanInviteMembersChange,
  onSeatPlanChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const base = `/organisations/${organisation.id}`;
  const memberSeat = membership?.status === 'active';

  const [overview, setOverview] =
    useState<OrganisationCreditsOverviewResponse | null>(null);
  const [groups, setGroups] = useState<OrganisationGroup[]>([]);
  const [groupsAvailable, setGroupsAvailable] = useState(false);
  const [billing, setBilling] = useState<OrganisationBillingSummary | null>(
    null,
  );
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);
  const [paymentCards, setPaymentCards] = useState<PaymentCards>(NO_CARDS);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  // The group routes answer 404 while Groups are off for the organisation
  // (403 for non-admins); null means "no Groups here", not a failed load.
  const loadGroups = useCallback(
    async (headers: {
      headers: { Authorization: string };
    }): Promise<OrganisationGroup[] | null> => {
      try {
        const { data } = await api.get<OrganisationGroupsResponse>(
          `${base}/groups`,
          headers,
        );
        return data.groups;
      } catch (err) {
        const status = httpStatus(err);
        if (status === 404 || status === 403) return null;
        throw err;
      }
    },
    [base],
  );

  const loadAdmin = useCallback(async () => {
    const headers = await authHeaders();
    const [ov, gr, bi, pc] = await Promise.all([
      api.get<OrganisationCreditsOverviewResponse>(`${base}/credits`, headers),
      loadGroups(headers),
      api.get<OrganisationBillingResponse>(`${base}/billing`, headers),
      api.get<OrganisationPaymentCardsResponse>(
        `${base}/billing/cards`,
        headers,
      ),
    ]);
    setOverview(ov.data);
    setGroups(gr ?? []);
    setGroupsAvailable(gr !== null);
    setBilling(bi.data.billing);
    setPaymentCards({
      cards: pc.data.cards,
      assignments: pc.data.assignments,
    });
    // Invoices only exist once Airwallex knows the organisation; a failure
    // here must not take the rest of Plan & billing down with it.
    if (bi.data.billing.hasBillingCustomer) {
      try {
        const { data } = await api.get<OrganisationInvoicesResponse>(
          `${base}/billing/invoices`,
          headers,
        );
        setInvoices(data.invoices);
      } catch (err) {
        console.error('Error loading organisation invoices:', err);
        setInvoices([]);
      }
    } else {
      setInvoices([]);
    }
  }, [authHeaders, base, loadGroups]);

  const load = useCallback(async () => {
    try {
      if (isAdmin) await loadAdmin();
    } catch (err) {
      console.error('Error loading organisation billing:', err);
      setFeedback({
        type: 'error',
        message: codedErrorMessage(err, 'Could not load organisation billing.'),
      });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, loadAdmin]);

  // Roster changes (seat kind, deactivation, …) move plan units and pool
  // figures, so refetch whenever the parent hands us a new members list.
  useEffect(() => {
    load();
  }, [load, members]);

  useEffect(() => {
    onGroupsChange?.(groups);
  }, [groups, onGroupsChange]);

  useEffect(() => {
    onGroupsAvailabilityChange?.(groupsAvailable);
  }, [groupsAvailable, onGroupsAvailabilityChange]);

  useEffect(() => {
    if (billing) onCanInviteMembersChange?.(billing.canInviteMembers);
  }, [billing, onCanInviteMembersChange]);

  const seatPlan = billing?.seatPlan ?? overview?.seatPlan ?? null;
  useEffect(() => {
    onSeatPlanChange?.(seatPlan);
  }, [seatPlan, onSeatPlanChange]);

  const run = useCallback(
    async (
      key: string,
      action: () => Promise<string | void>,
      fallback: string,
    ): Promise<boolean> => {
      setFeedback(null);
      setBusyKey(key);
      try {
        const message = await action();
        if (message) setFeedback({ type: 'success', message });
        return true;
      } catch (err) {
        console.error(fallback, err);
        setFeedback({
          type: 'error',
          message: codedErrorMessage(err, fallback),
        });
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  // ── Groups ────────────────────────────────────────────────────────────

  const applyPolicy = (data: OrganisationCreditPolicyResponse) => {
    setOverview((prev) =>
      prev ? { ...prev, policy: data.policy, overrides: data.overrides } : prev,
    );
  };

  const saveGroupOverride = async (
    groupId: string,
    override: GroupOverrideInput,
  ) => {
    const headers = await authHeaders();
    const clear = override.allocation === null && override.overflowCap === null;
    const body: SetOrganisationCreditPolicyOverrideRequest = override;
    const { data } = clear
      ? await api.delete<OrganisationCreditPolicyResponse>(
          `${base}/credits/policy/groups/${groupId}`,
          headers,
        )
      : await api.put<OrganisationCreditPolicyResponse>(
          `${base}/credits/policy/groups/${groupId}`,
          body,
          headers,
        );
    applyPolicy(data);
  };

  const handleCreateGroup = (
    input: CreateOrganisationGroupRequest,
    override: GroupOverrideInput,
  ) =>
    run(
      'group:new',
      async () => {
        const { data } = await api.post<OrganisationGroupResponse>(
          `${base}/groups`,
          input,
          await authHeaders(),
        );
        setGroups((prev) => [...prev, data.group]);
        if (override.allocation !== null || override.overflowCap !== null) {
          await saveGroupOverride(data.group.id, override);
        }
        await loadAdmin();
        return `Group “${data.group.name}” created.`;
      },
      'Could not create the group.',
    );

  const handleUpdateGroup = (
    group: OrganisationGroup,
    patch: UpdateOrganisationGroupRequest,
    override: GroupOverrideInput,
  ) =>
    run(
      group.id,
      async () => {
        const { data } = await api.patch<OrganisationGroupResponse>(
          `${base}/groups/${group.id}`,
          patch,
          await authHeaders(),
        );
        setGroups((prev) =>
          prev.map((g) => (g.id === group.id ? data.group : g)),
        );
        await saveGroupOverride(group.id, override);
        await loadAdmin();
        return `Group “${data.group.name}” updated.`;
      },
      'Could not update the group.',
    );

  const handleDeleteGroup = (group: OrganisationGroup) =>
    run(
      group.id,
      async () => {
        await api.delete(`${base}/groups/${group.id}`, await authHeaders());
        setGroups((prev) => prev.filter((g) => g.id !== group.id));
        await loadAdmin();
        return `Group “${group.name}” deleted. Its members are still in the organisation.`;
      },
      'Could not delete the group.',
    );

  const handleToggleGroupMember = (
    group: OrganisationGroup,
    member: OrganisationMember,
    assigned: boolean,
  ) =>
    run(
      `${group.id}:${member.id}`,
      async () => {
        const headers = await authHeaders();
        const { data } = assigned
          ? await api.post<OrganisationGroupResponse>(
              `${base}/groups/${group.id}/members`,
              { membershipIds: [member.id] },
              headers,
            )
          : await api.delete<OrganisationGroupResponse>(
              `${base}/groups/${group.id}/members/${member.id}`,
              headers,
            );
        setGroups((prev) =>
          prev.map((g) => (g.id === group.id ? data.group : g)),
        );
        await loadAdmin();
      },
      'Could not change that member’s groups.',
    );

  // ── Billing ───────────────────────────────────────────────────────────

  const checkout = (
    path: string,
    fallback: string,
    extra: Record<string, unknown> = {},
  ) =>
    run(
      'billing',
      async () => {
        const { data } = await api.post<OrganisationCheckoutResponse>(
          `${base}/billing/${path}`,
          { returnPath: organisationTabPath('plan'), ...extra },
          await authHeaders(),
        );
        window.location.assign(data.checkoutUrl);
      },
      fallback,
    );

  const handleAddCard = (purposes: OrganisationCardPurpose[]) =>
    checkout('payment-method', 'Could not open the add-card page.', {
      purposes,
    });

  const handleAssignCard = (purpose: OrganisationCardPurpose, cardId: string) =>
    run(
      'cards',
      async () => {
        const body: UpdateOrganisationPaymentCardsRequest = {
          assignments: { [purpose]: cardId },
        };
        const { data } = await api.patch<OrganisationPaymentCardsResponse>(
          `${base}/billing/cards`,
          body,
          await authHeaders(),
        );
        setPaymentCards({ cards: data.cards, assignments: data.assignments });
        const card = data.cards.find((c) => c.id === cardId);
        return card
          ? `${CARD_PURPOSE_LABELS[purpose].title} now uses ${cardLabel(card)}.`
          : 'Card updated.';
      },
      'Could not change that card.',
    );

  // ── Render ────────────────────────────────────────────────────────────

  const renderFeedback = () =>
    feedback && (
      <p
        className={`organisation-feedback ${feedback.type}`}
        role={feedback.type === 'error' ? 'alert' : 'status'}
      >
        {feedback.message}
      </p>
    );

  if (isAdmin && (view === 'team-assistants' || view === 'handbook'))
    return null;

  if (!isAdmin) {
    if (!memberSeat) return null;
    return (
      <OrganisationCardPanel
        organisation={organisation}
        members={members}
        groups={[]}
        groupsAvailable={groupsAvailable}
        isAdmin={false}
        memberSeat={memberSeat}
      />
    );
  }

  if (loading) {
    return view === 'members' ? null : (
      <p className='organisation-empty'>Loading…</p>
    );
  }

  if (view === 'members') return null;

  const paidSeats = seatMembers(members);

  const cardLine = (purpose: OrganisationCardPurpose) => (
    <OrganisationPaymentCardLine
      purpose={purpose}
      cards={paymentCards.cards}
      assignments={paymentCards.assignments}
      busy={busyKey === 'billing' || busyKey === 'cards'}
      onAssign={handleAssignCard}
      onAddCard={handleAddCard}
    />
  );
  // Before the subscription exists its checkout saves the first card, so
  // the card line only makes sense once the subscription is live.
  const subscriptionCardLine =
    billing && isSubscriptionLive(billing.subscriptionStatus)
      ? cardLine('subscription')
      : undefined;

  return (
    <>
      {view === 'plan' && billing && (
        <OrganisationSubscription
          billing={billing}
          teamAssistantSeats={billing.teamAssistantSeats}
          seatMembers={paidSeats}
          invoices={invoices}
          busy={busyKey === 'billing'}
          paymentCard={subscriptionCardLine}
          onSubscribe={() =>
            checkout('subscribe', 'Could not start the subscription checkout.')
          }
        />
      )}

      {groupsAvailable && view === 'groups' && (
        <OrganisationGroups
          groups={groups}
          overrides={overview?.overrides ?? []}
          members={members}
          defaultPlanMultiplier={overview?.policy.defaultPlanMultiplier ?? 1}
          seatPlan={overview?.seatPlan ?? null}
          busyKey={busyKey}
          onCreate={handleCreateGroup}
          onUpdate={handleUpdateGroup}
          onDelete={handleDeleteGroup}
          onToggleMember={handleToggleGroupMember}
        />
      )}

      {view === 'purchases' && (
        <OrganisationCardPanel
          organisation={organisation}
          members={members}
          groups={groups}
          groupsAvailable={groupsAvailable}
          isAdmin
          memberSeat={memberSeat}
          paymentCards={paymentCards}
          cardsBusy={busyKey === 'billing' || busyKey === 'cards'}
          onSetActiveCard={(cardId) =>
            handleAssignCard('memberSpending', cardId)
          }
          onAddCard={() =>
            handleAddCard(
              paymentCards.cards.length === 0
                ? [...ORGANISATION_CARD_PURPOSES]
                : ['memberSpending'],
            )
          }
        />
      )}

      {renderFeedback()}
    </>
  );
};

export default OrganisationCreditsPanel;
